import { Controller } from "@hotwired/stimulus"
import { setOptions, importLibrary } from "@googlemaps/js-api-loader"

// Center of the contiguous US, used until the pins give the map something to fit.
const US_CENTER = { lat: 39.5, lng: -98.35 }
const US_ZOOM = 4
// A lone city zooms in to roughly metro level rather than to the street.
const SINGLE_CITY_ZOOM = 8
const FIT_PADDING = 60
// Up to this many advisors show as stacked photos on one pin; the rest are counted in the badge.
const MAX_STACKED_AVATARS = 3

// The loader may only be configured once per page, and this controller can connect many times.
let loaderConfigured = false

// Draws won deals on a Google map: one advanced marker per client city, made of the photos of the
// advisors who sold there, with a popup listing that city's deals. All data arrives from the view
// model as the cities value; this controller only draws it.
export default class extends Controller {
  static values = { apiKey: String, mapId: String, cities: Array }

  async connect() {
    this.markers = []

    try {
      if (!loaderConfigured) {
        setOptions({ key: this.apiKeyValue, v: "weekly" })
        loaderConfigured = true
      }

      const [{ Map, InfoWindow }, { AdvancedMarkerElement }, { LatLngBounds }] = await Promise.all([
        importLibrary("maps"),
        importLibrary("marker"),
        importLibrary("core")
      ])
      // Turbo can swap the frame out while the library is still loading.
      if (!this.element.isConnected) return

      this.map = new Map(this.element, {
        mapId: this.mapIdValue,
        center: US_CENTER,
        zoom: US_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: "cooperative"
      })
      this.infoWindow = new InfoWindow()

      this.citiesValue.forEach((city) => this.addCityMarker(city, AdvancedMarkerElement))
      this.fitToCities(LatLngBounds)
    } catch (error) {
      console.error("Deal map failed to load", error)
      this.element.replaceChildren(this.textElement("div", "The map could not be loaded.", "maps-deal-map-error"))
    }
  }

  disconnect() {
    this.markers?.forEach((marker) => { marker.map = null })
    this.markers = []
    this.infoWindow?.close()
    this.map = null
  }

  addCityMarker(city, AdvancedMarkerElement) {
    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: { lat: city.latitude, lng: city.longitude },
      content: this.pinElement(city),
      title: `${city.label}: ${this.dealCountLabel(city.deals.length)}`,
      gmpClickable: true
    })

    marker.addEventListener("gmp-click", () => {
      this.infoWindow.setContent(this.infoElement(city))
      this.infoWindow.open({ anchor: marker, map: this.map })
    })

    this.markers.push(marker)
  }

  fitToCities(LatLngBounds) {
    const cities = this.citiesValue
    if (cities.length === 0) return

    if (cities.length === 1) {
      this.map.setCenter({ lat: cities[0].latitude, lng: cities[0].longitude })
      this.map.setZoom(SINGLE_CITY_ZOOM)
      return
    }

    const bounds = new LatLngBounds()
    cities.forEach((city) => bounds.extend({ lat: city.latitude, lng: city.longitude }))
    this.map.fitBounds(bounds, FIT_PADDING)
  }

  // The pin: each distinct advisor's photo, overlapped, plus a badge when the city has more than
  // one deal.
  pinElement(city) {
    const pin = this.textElement("div", "", "maps-deal-map-pin")
    const advisors = this.distinctAdvisors(city.deals)

    const stack = this.textElement("div", "", "maps-deal-map-pin-stack")
    advisors.slice(0, MAX_STACKED_AVATARS).forEach((deal) => stack.append(this.avatarElement(deal, "maps-deal-map-pin-avatar")))
    pin.append(stack)

    if (city.deals.length > 1) {
      pin.append(this.textElement("span", String(city.deals.length), "maps-deal-map-pin-count"))
    }
    pin.append(this.textElement("span", "", "maps-deal-map-pin-tail"))

    return pin
  }

  // Popup content is built from DOM nodes and textContent, never innerHTML: brand and client names
  // are data.
  infoElement(city) {
    const info = this.textElement("div", "", "maps-deal-map-info")
    const territories = city.deals.reduce((sum, deal) => sum + deal.territories, 0)

    info.append(
      this.textElement("div", city.label, "maps-deal-map-info-title"),
      this.textElement("div", `${this.dealCountLabel(city.deals.length)} · ${this.territoryLabel(territories)}`, "maps-deal-map-info-subtitle")
    )

    const list = this.textElement("ul", "", "maps-deal-map-info-list")
    city.deals.forEach((deal) => list.append(this.dealElement(deal)))
    info.append(list)

    return info
  }

  dealElement(deal) {
    const item = this.textElement("li", "", "maps-deal-map-info-deal")
    const body = this.textElement("div", "", "maps-deal-map-info-deal-body")

    const heading = this.textElement("div", deal.brand, "maps-deal-map-info-brand")
    if (deal.stage) heading.append(this.textElement("span", deal.stage, "maps-deal-map-info-stage"))

    const facts = [this.territoryLabel(deal.territories)]
    if (deal.awarded_on) facts.push(`Awarded ${deal.awarded_on}`)

    body.append(heading, this.textElement("div", facts.join(" · "), "maps-deal-map-info-facts"))
    if (deal.client_name) body.append(this.textElement("div", `Client: ${deal.client_name}`, "maps-deal-map-info-line"))
    body.append(this.textElement("div", `Advisor: ${deal.advisor_name}`, "maps-deal-map-info-line"))

    item.append(this.avatarElement(deal, "maps-deal-map-info-avatar"), body)
    return item
  }

  // A photo when the advisor has one, otherwise their initials; a photo that fails to load falls
  // back to initials too.
  avatarElement(deal, className) {
    const initials = () => {
      const fallback = this.textElement("span", deal.advisor_initials, `${className} maps-deal-map-initials`)
      fallback.style.backgroundColor = this.colorFor(deal.advisor_name)
      fallback.title = deal.advisor_name
      return fallback
    }
    if (!deal.advisor_image_url) return initials()

    const image = document.createElement("img")
    image.className = className
    image.src = deal.advisor_image_url
    image.alt = deal.advisor_name
    image.title = deal.advisor_name
    image.addEventListener("error", () => image.replaceWith(initials()), { once: true })
    return image
  }

  distinctAdvisors(deals) {
    const seen = new Set()
    return deals.filter((deal) => {
      if (seen.has(deal.advisor_name)) return false
      seen.add(deal.advisor_name)
      return true
    })
  }

  // A stable color per advisor, so the same initials look the same on every pin.
  colorFor(name) {
    let hash = 0
    for (const character of String(name)) hash = (hash * 31 + character.charCodeAt(0)) % 360
    return `hsl(${hash}, 45%, 45%)`
  }

  dealCountLabel(count) {
    return `${count} ${count === 1 ? "deal" : "deals"}`
  }

  territoryLabel(count) {
    return `${count} ${count === 1 ? "territory" : "territories"}`
  }

  textElement(tag, text, className) {
    const element = document.createElement(tag)
    if (className) element.className = className
    if (text) element.textContent = text
    return element
  }
}
